import React, { useEffect, useState } from "react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import TestimonialFormModal from "../components/site/testimonials/TestimonialFormModal";
import PageSeo from "../components/site/PageSeo";
import { SITE_URL, ORGANIZATION, PERSON } from "../data/entityGraph";
import { Quote } from "lucide-react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/track";

/**
 * /voices — dedicated page for approved testimonials.
 *
 * Pulls /api/testimonials and renders all approved voices with the
 * preferred display order: Amara K., Marcus D., Imani R. first.
 * The homepage shows only three teasers; this is the long-form
 * companion. Each card includes the role, the testimonial, the
 * shift, and the advice fields from the admin schema so the full
 * voice comes through.
 */
const VoicesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const openForm = () => {
    trackEvent("voices_add_words_clicked");
    setFormOpen(true);
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/testimonials")
      .then((res) => {
        if (cancelled) return;
        const preferred = ["Amara K.", "Marcus D.", "Imani R."];
        const list = res.data || [];
        const sorted = [...list].sort((a, b) => {
          const ai = preferred.indexOf(a.name);
          const bi = preferred.indexOf(b.name);
          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
        setItems(sorted);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <PageSeo
        title="Voices and Reflections | Authentic Momentum"
        description="Reflections from people who walked the Authentic Momentum path and let the signal lead. Read the voices, then add your own."
        canonical={`${SITE_URL}/voices`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${SITE_URL}/voices#collection`,
            url: `${SITE_URL}/voices`,
            name: "Voices and Reflections | Authentic Momentum",
            about: { ...PERSON },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Voices", item: `${SITE_URL}/voices` },
            ],
          },
          { "@context": "https://schema.org", ...ORGANIZATION },
        ]}
      />
      <Navigation />
      <main className="relative pt-32 pb-32 min-h-screen">
        <section className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              Voices & Reflections
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl text-themed leading-tight tracking-tight font-light">
              What clarity{" "}
              <span className="italic gold-gradient-text">sounds like.</span>
            </h1>
            <p className="mt-8 text-white/85 max-w-2xl mx-auto leading-relaxed">
              Words from people who walked the path and let the signal lead. Add your own when something here meets you.
            </p>
          </Reveal>
        </section>

        {/* Add Your Words — surfaced HIGH on the page so visitors who
            arrived to share their own reflection can do so without
            scrolling past every existing voice first. Mirrored at the
            bottom for visitors who only realize they have words to
            add after reading the rest. */}
        <section
          id="add-your-words"
          className="relative max-w-3xl mx-auto px-6 md:px-10 mt-14"
        >
          <Reveal>
            <div
              data-testid="voices-add-words-card-top"
              className="glass-card p-7 sm:p-8 flex flex-col sm:flex-row items-center gap-6 sm:gap-8 text-center sm:text-left"
            >
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-2">
                  Return Your Reflection
                </p>
                <h2 className="font-serif text-xl sm:text-2xl text-themed leading-tight font-light">
                  Have your own voice to add?
                </h2>
                <p className="text-themed-muted text-[14px] leading-relaxed mt-2 max-w-xl">
                  Short or long. Anonymous if you prefer. Reviewed before it lands on the site.
                </p>
              </div>
              <button
                type="button"
                data-testid="voices-add-words-cta"
                onClick={openForm}
                className="btn-gold whitespace-nowrap"
              >
                Add Your Reflection
              </button>
            </div>
          </Reveal>
        </section>

        <section
          data-testid="voices-grid"
          className="relative max-w-6xl mx-auto px-6 md:px-10 mt-16"
        >
          {loading ? (
            <p className="text-center text-themed-muted">Loading voices…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-themed-muted">
              No approved voices yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map((t, i) => (
                <Reveal key={t.id || i} delay={(i % 2) * 80}>
                  <figure
                    data-testid={`voice-card-${i}`}
                    className="glass-card h-full p-8 flex flex-col"
                  >
                    <Quote
                      size={20}
                      strokeWidth={1.4}
                      className="text-gold/60 mb-5"
                      aria-hidden
                    />
                    <blockquote className="text-themed-muted text-[15px] leading-relaxed">
                      {t.testimonial}
                    </blockquote>
                    {t.shift ? (
                      <div className="mt-6 pt-5 border-t border-gold/15">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mb-2">
                          The Shift
                        </div>
                        <p className="text-themed-muted text-[13px] italic leading-relaxed">
                          {t.shift}
                        </p>
                      </div>
                    ) : null}
                    {t.advice ? (
                      <div className="mt-5">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mb-2">
                          What They Would Tell You
                        </div>
                        <p className="text-themed-muted text-[13px] leading-relaxed">
                          {t.advice}
                        </p>
                      </div>
                    ) : null}
                    <figcaption className="mt-6 pt-5 border-t border-gold/15">
                      <div className="text-themed text-sm font-serif">
                        {t.name}
                      </div>
                      {t.role ? (
                        <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mt-1">
                          {t.role}
                        </div>
                      ) : null}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          )}
        </section>

        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-24">
          <Reveal>
            <div
              data-testid="voices-add-words-card"
              className="glass-card p-8 sm:p-10 text-center"
            >
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">
                One more pass
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl text-themed leading-tight font-light mb-4">
                Did the path move something for you?
              </h2>
              <p className="text-themed-muted leading-relaxed max-w-xl mx-auto mb-7">
                If something here shifted how you see your current friction, the next person on this path is helped most by your honest words.
              </p>
              <button
                type="button"
                data-testid="voices-add-words-cta-bottom"
                onClick={openForm}
                className="btn-gold"
              >
                Add Your Reflection
              </button>
            </div>
          </Reveal>
        </section>

        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-24 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              Begin
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-themed leading-tight font-light">
              Hear yourself in one of these{" "}
              <span className="italic gold-gradient-text">voices?</span>
            </h2>
            <p className="mt-6 text-themed-muted leading-relaxed">
              Start with the Snapshot to find the signal underneath your
              current friction.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/frequency-snapshot"
                data-testid="voices-cta-snapshot"
                onClick={() => trackEvent("voices_snapshot_cta_clicked")}
                className="btn-gold"
              >
                Take the Clarity Snapshot
              </a>
              <button
                type="button"
                data-testid="voices-cta-orientation-reset"
                onClick={() => {
                  trackEvent("voices_orientation_reset_cta_clicked");
                  window.dispatchEvent(
                    new CustomEvent("am:open-orientation-reset-checkout")
                  );
                }}
                className="btn-ghost-gold"
              >
                Book the Clarity Reset Session · $149
              </button>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
      <OrientationResetCheckoutHost />
      <TestimonialFormModal open={formOpen} onClose={() => setFormOpen(false)} />
    </>
  );
};

export default VoicesPage;
